/**
 * Human sentences for source errors (api.md amendments "Refusal reasons"): the relay and the IMAP bridge
 * answer `fetch_blocked` with `details.reason`; every other code gets one plain sentence. The engine's
 * own message (safe to show) is kept when it says more than the code.
 */

import { toDigesterError, type DigesterErrorShape } from '@digester/core';

type Via = 'relay' | 'imap';

const RELAY_REASONS: Readonly<Record<string, string>> = {
  private_address: 'That address points into a private network. Digester’s relay only fetches public internet addresses.',
  own_address: 'The relay does not fetch Digester’s own addresses.',
  port: 'The relay only fetches from the standard web ports (80 and 443).',
  scheme: 'Only http:// and https:// addresses can be fetched.',
  userinfo: 'Addresses with a user name or password in them are refused. Remove the part before “@”.',
  url_too_long: 'That address is too long to fetch.',
  invalid_url: 'That is not a valid web address.',
  post_not_allowed: 'The relay does not send that kind of request to this site.',
  post_too_large: 'The request is too large for the relay.',
  body_on_get: 'The relay refused a malformed request.',
  too_many_redirects: 'The site redirected too many times. Try the final address directly.',
  content_type: 'The site answered with something that is neither a feed nor a page (files and images are refused).',
  too_large: 'The answer is larger than the relay accepts.',
};

const IMAP_REASONS: Readonly<Record<string, string>> = {
  port: 'The mail bridge only connects to the standard IMAP ports: 993 for SSL/TLS, 143 for STARTTLS.',
  insecure: 'The mail bridge refuses unencrypted connections. Choose SSL/TLS or STARTTLS.',
  invalid_host: 'That mail server name is not valid, or it points into a private network.',
};

const CODES: Readonly<Record<string, string>> = {
  fetch_failed: 'The source could not be reached.',
  parse_failed: 'The source answered, but not with anything Digester can read.',
  secret_missing: 'This source needs its password or token on this device.',
  source_auth_failed: 'The server rejected the user name, password or token.',
  rate_limited: 'The source asked us to slow down. Digester tries again later.',
  unsupported: 'This kind of source cannot be pulled here.',
  unauthenticated: 'Sign in to use this.',
  not_found: 'Nothing was found at that address.',
};

/** One sentence (or two) for the user. `via` picks the reason table of fetch_blocked. */
export function describeSourceError(error: unknown, via: Via = 'relay'): string {
  const e: DigesterErrorShape = toDigesterError(error).toShape();
  const reason = typeof e.details?.reason === 'string' ? e.details.reason : null;
  if (e.code === 'fetch_blocked') {
    const table = via === 'imap' ? IMAP_REASONS : RELAY_REASONS;
    return (reason && table[reason]) ?? (via === 'imap' ? 'The mail bridge refused that server.' : 'The relay refused that address.');
  }
  if (e.code === 'fetch_failed') {
    const status = typeof e.details?.status === 'number' ? e.details.status : null;
    if (status === 404 || status === 410) return `The address answered ${status}: there is nothing there (any more).`;
    if (reason === 'timeout') return 'The source took too long to answer.';
    if (status) return `The source answered with an error (HTTP ${status}).`;
  }
  const base = CODES[e.code];
  if (e.code === 'invalid_input' || e.code === 'parse_failed' || e.code === 'unsupported') return e.message || base || 'Something is missing.';
  return base ? (e.message && e.message !== base && e.code !== 'fetch_failed' ? `${base} ${e.message}` : base) : e.message;
}

/** For a stored status error ({ code, message }, no details). */
export function describeStatusError(error: { code: string; message: string }): string {
  if (error.code === 'secret_missing') return 'Add the password or token on this device.';
  if (error.code === 'fetch_blocked') return `The relay refused it: ${error.message}`;
  const base = CODES[error.code];
  return base && !error.message.startsWith(base) ? `${base} ${error.message}` : error.message || base || error.code;
}
